import {useCallback, useMemo, useState} from 'react';
import {NovaValidationContext} from '../shared/novaInputValidation';
import {NovaTestTask} from '../shared/novaTaskVariables';
import {buildGroundingBreakdown, NovaGroundingBreakdown, NovaGroundingInputs} from '../shared/novaGroundingBreakdown';

export const NovaDryRunState = {
	NoTask: 'NO_TASK',
	Ready: 'READY',
	Running: 'RUNNING',
	Result: 'RESULT',
	Error: 'ERROR',
} as const;
export type NovaDryRunState = (typeof NovaDryRunState)[keyof typeof NovaDryRunState];

export interface NovaDryRunTurn {
	// Null on the opening turn; the author's text on a follow-up.
	message: string | null;
	output: string;
	ranAt: string;
	// Recomputed per turn: a follow-up is more per-run input, so its grounding is its own.
	breakdown: NovaGroundingBreakdown;
}

// One run as it was asked for, so a failure can be repeated exactly.
interface NovaDryRunAttempt {
	message: string | null;
	replaceTurns: boolean;
}

export interface NovaRunAgentArguments {
	taskId: string;
	message: string | null;
}

export interface NovaRunAgentResult {
	output: string;
	ranAt: string;
}

// Injected rather than imported so this hook has no Relay dependency and can be tested with a plain
// mock — the same reason NovaStudioShell takes its mutations from the page above it.
export type NovaRunAgent = (args: NovaRunAgentArguments) => Promise<NovaRunAgentResult>;

export interface UseNovaDryRunOptions {
	inputs: NovaGroundingInputs;
	validationContext: NovaValidationContext;
	runAgent: NovaRunAgent;
}

export interface NovaDryRunController {
	state: NovaDryRunState;
	selectedTask: NovaTestTask | null;
	turns: NovaDryRunTurn[];
	canRun: boolean;
	selectTask: (task: NovaTestTask) => void;
	clearTask: () => void;
	run: () => Promise<void>;
	followUp: (message: string) => Promise<void>;
	retry: () => Promise<void>;
	clearConversation: () => void;
}

// The run lifecycle for one agent: which task is selected, the turns so far, and the actions that
// move between them. Nothing here writes to the task — the injected `runAgent` is a dry run by
// construction, and this hook never sees a write path at all.
export function useNovaDryRun(options: UseNovaDryRunOptions): NovaDryRunController {
	const {inputs, validationContext, runAgent} = options;
	const [selectedTask, setSelectedTask] = useState<NovaTestTask | null>(null);
	const [turns, setTurns] = useState<NovaDryRunTurn[]>([]);
	const [isRunning, setIsRunning] = useState(false);
	const [hasFailed, setHasFailed] = useState(false);
	// The attempt Retry replays. Held rather than reconstructed because a failed follow-up is not the
	// opening turn: retrying it has to re-send that message and append to the conversation, not start
	// a fresh one over the top of the turns already on screen.
	const [lastAttempt, setLastAttempt] = useState<NovaDryRunAttempt | null>(null);

	const state = useMemo((): NovaDryRunState => {
		if (isRunning) {
			return NovaDryRunState.Running;
		}
		if (hasFailed) {
			return NovaDryRunState.Error;
		}
		if (selectedTask === null) {
			return NovaDryRunState.NoTask;
		}
		return turns.length > 0 ? NovaDryRunState.Result : NovaDryRunState.Ready;
	}, [isRunning, hasFailed, selectedTask, turns.length]);

	// `replaceTurns` distinguishes Run from a follow-up: Run starts the conversation over, a follow-up
	// extends it. Both go through one path so the failure handling cannot diverge.
	const execute = useCallback(
		async (message: string | null, replaceTurns: boolean): Promise<void> => {
			if (selectedTask === null) {
				return;
			}
			setIsRunning(true);
			setHasFailed(false);
			setLastAttempt({message, replaceTurns});
			try {
				const result = await runAgent({taskId: selectedTask.taskId, message});
				const turn: NovaDryRunTurn = {
					message,
					output: result.output,
					ranAt: result.ranAt,
					breakdown: buildGroundingBreakdown(inputs, selectedTask, validationContext),
				};
				setTurns(previous => (replaceTurns ? [turn] : [...previous, turn]));
			} catch (error) {
				// A failed run leaves no turn at all: a half-rendered card would read as an answer.
				setHasFailed(true);
			} finally {
				setIsRunning(false);
			}
		},
		[selectedTask, runAgent, inputs, validationContext]
	);

	const selectTask = useCallback((task: NovaTestTask) => {
		// A conversation is about one task, so pointing the agent somewhere else starts fresh rather
		// than carrying turns across a change of subject.
		setSelectedTask(task);
		setTurns([]);
		setHasFailed(false);
		setLastAttempt(null);
	}, []);

	const clearTask = useCallback(() => {
		setSelectedTask(null);
		setTurns([]);
		setHasFailed(false);
		setLastAttempt(null);
	}, []);

	const run = useCallback(() => execute(null, true), [execute]);
	const followUp = useCallback((message: string) => execute(message, false), [execute]);
	// Retry repeats the attempt that failed, whichever it was. A failed follow-up leaves the earlier
	// turns intact and still on screen, so replaying it as a fresh opening turn would discard the
	// conversation and never re-send the message the author actually lost.
	const retry = useCallback(
		() => (lastAttempt === null ? Promise.resolve() : execute(lastAttempt.message, lastAttempt.replaceTurns)),
		[execute, lastAttempt]
	);

	const clearConversation = useCallback(() => {
		setTurns([]);
		setHasFailed(false);
		setLastAttempt(null);
	}, []);

	return {
		state,
		selectedTask,
		turns,
		canRun: selectedTask !== null && !isRunning,
		selectTask,
		clearTask,
		run,
		followUp,
		retry,
		clearConversation,
	};
}
